import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import {
  TransactionType,
  TransactionValue,
} from '@/reducers/transactionReducer'
import {
  getLegacyCadcTokenAddress,
  getTokenExplorerUrl,
  NetworkOptions,
  NetworkValue,
  NewCadcTokenAddresses,
} from '@/utils/currencies'

const LINK_PATTERN =
  /(\[\[[^\]]+\]\]|https?:\/\/[^\s]*[^\s.,;:!?)]|[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[\w-]+)/g

const EMAIL_PATTERN = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[\w-]+$/

const renderWithLinks = (text: string, legacyContractUrl: string | undefined) =>
  text.split(LINK_PATTERN).map((part, index) => {
    if (part.startsWith('[[')) {
      const label = part.slice(2, -2)
      return legacyContractUrl ? (
        <a
          key={index}
          href={legacyContractUrl}
          target="_blank"
          rel="noreferrer"
          className="underline"
          data-testid="link-legacy-cadc-contract"
        >
          {label}
        </a>
      ) : (
        label
      )
    }
    if (part.startsWith('http')) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          {part}
        </a>
      )
    }
    if (EMAIL_PATTERN.test(part)) {
      return (
        <a key={index} href={`mailto:${part}`} className="underline">
          {part}
        </a>
      )
    }
    return part
  })

const TONE_CLASSES = {
  info: 'border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
  blocked:
    'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200',
} as const

type Tone = keyof typeof TONE_CLASSES

type NoticeContent = {
  tone: Tone
  body: string[]
  address?: string
  footnote?: string
}

type CadcContractNoticeProps = {
  network: NetworkValue
  transactionType: TransactionValue
}

export const CadcContractNotice = memo(
  ({ network, transactionType }: CadcContractNoticeProps) => {
    const { t } = useTranslation()
    const {
      enableSendNewCadc,
      enableReceiveNewCadc,
      enableRejectOldCadc,
      isLoading,
    } = useFeatureFlags()

    const newContract = NewCadcTokenAddresses[network]
    const networkName = NetworkOptions[network].name
    const legacyContract = getLegacyCadcTokenAddress(network)
    const legacyContractUrl = legacyContract
      ? getTokenExplorerUrl(network, legacyContract)
      : undefined

    const title = enableRejectOldCadc
      ? t('buySell.cadcContract.titleMoved')
      : t('buySell.cadcContract.title')

    const content = (): NoticeContent | null => {
      if (transactionType === TransactionType.BUY) {
        if (!enableSendNewCadc) return null
        return newContract
          ? {
              tone: 'info',
              body: [
                t('buySell.cadcContract.buyNewContractBody', {
                  network: networkName,
                }),
              ],
              address: newContract,
              footnote: enableRejectOldCadc
                ? t('buySell.cadcContract.legacySwapViaSupport')
                : t('buySell.cadcContract.buyNewContractFootnote'),
            }
          : {
              tone: 'blocked',
              body: [
                t('buySell.cadcContract.buyUnchangedNetworkBody', {
                  network: networkName,
                }),
              ],
            }
      }

      if (enableRejectOldCadc) {
        return newContract
          ? {
              tone: 'info',
              body: [
                t('buySell.cadcContract.legacySwapViaSupport'),
                t('buySell.cadcContract.sellNewContractOnlyBody'),
              ],
              address: newContract,
            }
          : {
              tone: 'blocked',
              body: [
                t('buySell.cadcContract.sellPausedBody', {
                  network: networkName,
                }),
              ],
              footnote: t('buySell.cadcContract.sellPausedFootnote'),
            }
      }

      return enableReceiveNewCadc
        ? {
            tone: 'info',
            body: [
              t('buySell.cadcContract.sellEitherContractSwapBody'),
              t('buySell.cadcContract.sellEitherContractBody'),
            ],
            footnote: t('buySell.cadcContract.sellEitherContractFootnote'),
          }
        : null
    }

    const notice = isLoading ? null : content()

    if (!notice) return null

    return (
      <div
        role="status"
        className={`my-2 flex flex-col gap-1 rounded border px-3 py-2 text-sm ${TONE_CLASSES[notice.tone]}`}
        data-testid="banner-cadc-contract"
      >
        <span className="font-bold">{title}</span>
        {notice.body.map((line, index) => (
          <span key={index} className="break-words">
            {renderWithLinks(line, legacyContractUrl)}
            {notice.address && index === notice.body.length - 1 && (
              <>
                {' '}
                <a
                  href={getTokenExplorerUrl(network, notice.address)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono break-all underline"
                  data-testid="link-cadc-contract-address"
                >
                  {notice.address}
                </a>
              </>
            )}
          </span>
        ))}
        {notice.footnote && (
          <span className="break-words opacity-80">
            {renderWithLinks(notice.footnote, legacyContractUrl)}
          </span>
        )}
      </div>
    )
  }
)

CadcContractNotice.displayName = 'CadcContractNotice'
